import { useState } from 'react';
import { useNavigate } from 'react-router';
import { apiClient } from '../lib/api-client';

function CreateCurrencyPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    symbol: '',
    useDecimals: true,
    decimalPlaces: 2,
    displayOrder: 999,
    isActive: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate required fields
      if (!formData.code || !formData.name || !formData.symbol) {
        throw new Error('Code, name, and symbol are required');
      }

      // Validate code format (3 characters, lowercase)
      if (formData.code.length !== 3) {
        throw new Error('Currency code must be exactly 3 characters (ISO 4217)');
      }

      const result = await apiClient.createCurrency({
        code: formData.code.toLowerCase(),
        name: formData.name,
        symbol: formData.symbol,
        useDecimals: formData.useDecimals,
        decimalPlaces: formData.decimalPlaces,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive
      });

      setSuccess(`Currency created successfully: ${result.currency.code.toUpperCase()}`);

      setTimeout(() => {
        navigate('/settings');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create currency');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className='mx-auto max-w-2xl space-y-6'>
      <div className='flex items-center gap-4'>
        <button
          onClick={() => navigate('/settings')}
          className='text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
        >
          ← Back to Service Settings
        </button>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>Add New Currency</h1>
      </div>

      {error && (
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950'>
          <p className='text-red-800 dark:text-red-200'>{error}</p>
        </div>
      )}

      {success && (
        <div className='rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950'>
          <p className='text-green-800 dark:text-green-200'>{success}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='space-y-6 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'
      >
        {/* Currency Code */}
        <div>
          <label
            htmlFor='code'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Currency Code <span className='text-red-500'>*</span>
          </label>
          <input
            type='text'
            id='code'
            value={formData.code}
            onChange={e => handleChange('code', e.target.value.toLowerCase())}
            maxLength={3}
            required
            placeholder='usd'
            className='w-full rounded-md border border-gray-300 px-3 py-2 font-mono uppercase focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            ISO 4217 code (3 characters, e.g., USD, JPY, EUR)
          </p>
        </div>

        {/* Currency Name */}
        <div>
          <label
            htmlFor='name'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Currency Name <span className='text-red-500'>*</span>
          </label>
          <input
            type='text'
            id='name'
            value={formData.name}
            onChange={e => handleChange('name', e.target.value)}
            required
            placeholder='US Dollar'
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>Full name in English</p>
        </div>

        {/* Currency Symbol */}
        <div>
          <label
            htmlFor='symbol'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Currency Symbol <span className='text-red-500'>*</span>
          </label>
          <input
            type='text'
            id='symbol'
            value={formData.symbol}
            onChange={e => handleChange('symbol', e.target.value)}
            required
            placeholder='$'
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>Symbol (e.g., $, ¥, €, £)</p>
        </div>

        {/* Decimal Configuration */}
        <div className='border-t pt-4 dark:border-gray-700'>
          <div className='mb-3 flex items-center gap-3'>
            <input
              type='checkbox'
              id='useDecimals'
              checked={formData.useDecimals}
              onChange={e => handleChange('useDecimals', e.target.checked)}
              className='rounded border-gray-300 dark:border-gray-600'
            />
            <label
              htmlFor='useDecimals'
              className='text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              Use Decimal Places
            </label>
          </div>

          {formData.useDecimals && (
            <div className='ml-6'>
              <label
                htmlFor='decimalPlaces'
                className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
              >
                Decimal Places
              </label>
              <select
                id='decimalPlaces'
                value={formData.decimalPlaces}
                onChange={e => handleChange('decimalPlaces', parseInt(e.target.value))}
                className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
              >
                <option value={0}>0 (No decimals)</option>
                <option value={1}>1 digit</option>
                <option value={2}>2 digits</option>
                <option value={3}>3 digits</option>
                <option value={4}>4 digits</option>
              </select>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                Most currencies use 2 decimal places. JPY and KRW typically use 0.
              </p>
            </div>
          )}
        </div>

        {/* Display Order */}
        <div>
          <label
            htmlFor='displayOrder'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Display Order
          </label>
          <input
            type='number'
            id='displayOrder'
            value={formData.displayOrder}
            onChange={e => handleChange('displayOrder', parseInt(e.target.value) || 0)}
            min='0'
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Lower numbers appear first (e.g., 1, 2, 3...)
          </p>
        </div>

        {/* Active Status */}
        <div className='flex items-center gap-3'>
          <input
            type='checkbox'
            id='isActive'
            checked={formData.isActive}
            onChange={e => handleChange('isActive', e.target.checked)}
            className='rounded border-gray-300 dark:border-gray-600'
          />
          <label
            htmlFor='isActive'
            className='text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Active (available for use in plans)
          </label>
        </div>

        {/* Submit */}
        <div className='flex gap-4'>
          <button
            type='submit'
            disabled={loading}
            className='flex-1 rounded-md bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-gray-600'
          >
            {loading ? 'Creating...' : 'Create Currency'}
          </button>
          <button
            type='button'
            onClick={() => navigate('/settings')}
            className='rounded-md bg-gray-200 px-6 py-3 text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateCurrencyPage;
